import { defineStore } from 'pinia'

// Define a simpler state interface
interface ModalState {
  // Use an array of modal names that are currently open
  openModalNames: string[]
  // Store modal params separately for each modal
  modalParams: Record<string, object>
}

/**
 * Helper function to open a modal
 * @param {string} name - The name of the modal to open
 * @param {object} params - Optional parameters to pass to the modal
 */
export const openModal = (name: string, params: object = {}): void => {
  useModalStore().open(name, params)
}

/**
 * Helper function to close a modal
 * @param {string} name - The name of the modal to close
 */
export const closeModal = (name: string): void => {
  useModalStore().close(name)
}

/**
 * Store for managing global modals and dialogs
 */
export const useModalStore = defineStore('modal', {
  state: (): ModalState => ({
    openModalNames: [],
    modalParams: {},
  }),

  getters: {
    /**
     * Check if a modal is currently open
     * @param {ModalState} state - The state of the store
     * @returns {boolean} True if the modal is open, false otherwise
     */
    isOpen:
      (state: ModalState) =>
      (name: string): boolean => {
        return state.openModalNames.includes(name)
      },

    /**
     * Get parameters for a specific modal
     * @param {ModalState} state - The state of the store
     * @returns {any} The parameters for the specified modal, or an empty object if none exist
     */
    getModalParams:
      (state: ModalState) =>
      (name: string): any => {
        return state.modalParams[name] || {}
      },
  },

  actions: {
    /**
     * Open a modal with the given name and optional parameters
     * @param {string} name - The name of the modal to open
     * @param {object} params - Optional parameters to pass to the modal
     */
    open(name: string, params: object = {}) {
      // Add to open modals if not already there
      if (!this.openModalNames.includes(name)) {
        this.openModalNames.push(name)
      }

      // Store parameters
      this.modalParams = { ...this.modalParams, [name]: params }
    },

    /**
     * Close a modal with the given name
     * @param {string} name - The name of the modal to close
     */
    close(name: string) {
      // Remove from open modals array
      const index = this.openModalNames.indexOf(name)
      if (index !== -1) {
        this.openModalNames.splice(index, 1)
      }

      // Remove parameters (create a new object to trigger reactivity)
      const newParams = { ...this.modalParams }
      delete newParams[name]
      this.modalParams = newParams
    },
  },
})
